<template>
  <m-dialog
    v-model:value="visible"
    center
    hide-header
    hide-footer
    fade-in
    @close="reset"
  >
    <div class="add-account-dialog">
      <div class="_header">
        <div class="_title">
          {{ t('addAccountDialog.title') }}
        </div>
        <m-btn
          class="_close"
          icon="close"
          fab
          small
          light
          hide-border
          :aria-label="t('general.close')"
          @click="dismiss"
        />
      </div>
      <account-infos-form
        :initial-company-name="companyName"
        :initial-company-size="companySize"
        :image-url="imageUrl"
        :submit-label="t('addAccountDialog.create')"
        @image-cropped="onImageCropped"
        @submit="onSubmit"
      />
    </div>
  </m-dialog>
</template>

<script setup>
import AccountInfosForm from '@/components/onboarding/AccountInfosForm.vue';
import useAccountSwitch from '@/composables/account-switch';
import useOverlaySpinner from '@/composables/overlay-spinner';
import useSnackBar from '@/composables/snackbar';
import { EventBus } from '@/lib/event-bus';
import { UID } from 'shared/api/query/constants';
import { account as accountConfig, accountSlug as accountSlugConfig } from 'shared/api/query/configs.json';
import { accountInfosUpdate } from '@/lib/account/account-infos';
import { children as accountSlugChildren } from '@/api/query/nebula/account-slug';
import { doAddAccount, dogma } from '@/api';
import { errorType } from '@/constants/sentinel.json';
import { logCatch } from '@/lib/logger/logger';
import { onBeforeUnmount, ref } from 'vue';
import { routeName } from 'shared/constants.json';
import { uploadFile } from '@/lib/image/image';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const snackbar = useSnackBar();
const { showOverlaySpinner, hideOverlaySpinner } = useOverlaySpinner();
const { switchAccount } = useAccountSwitch();

const visible = ref(false);

// Held here rather than in the form: the dialog is hidden while the workspace is created, which
// unmounts the form — a failed create has to come back with what the user typed.
const companyName = ref('');
const companySize = ref(null);
const imageUrl = ref('');

// The blob store is not scoped to an account, so the logo does not have to wait for one to exist.
// Uploading on crop leaves only the account write for the create itself, and lets the preview use
// the stored file instead of a data URL. This holds the upload itself rather than its result, so a
// submit that beats it to the finish waits instead of creating the workspace without a logo.
let uploadedImage = null;

// An account that was provisioned but never named. Retrying resumes on it instead of provisioning
// a second one.
let provisionedAccount = null;

const reset = () => {
  uploadedImage = null;
  imageUrl.value = '';
  companyName.value = '';
  companySize.value = null;
};

const dismiss = () => {
  visible.value = false;
  reset();
};

const provision = async () => {
  const response = await doAddAccount();
  if (response?.status === 200) {
    return response.data.account;
  }

  if (response?.status === 403 && response.data?.code === errorType.signupDomainBlocked) {
    snackbar.error(t('register.domainBlocked', { displayName: response.data.payload.DisplayName }));
  } else {
    snackbar.error();
  }
  return null;
};

const SLUG_RID = 1;

// The slug is not a plain edge: only the accountSlug create pipeline runs the enricher that makes
// it unique, and the account links the node it mints by a payload-scoped rid. So both have to
// travel in one bulk mutation, and the slug that comes back is the one that was assigned — ours
// only if it was still free.
const namePayloads = (account, infos) => {
  const { activeSlug: slug, accountSettings, ...infosUpdate } = accountInfosUpdate(infos);
  const activeSlug = { ...slug, rid: SLUG_RID, account: { uid: account.uid } };
  const update = {
    ...infosUpdate,
    uid: account.uid,
    activeSlug,
    accountSettings: { uid: account.accountSettings.uid, ...accountSettings },
  };

  return [
    {
      alias: 'createAccountSlug',
      model: accountSlugConfig.model,
      nodes: [activeSlug],
      attributes: accountSlugChildren,
      createHookParameter: { forceToSetSlug: true },
    },
    {
      alias: 'updateAccount',
      model: accountConfig.model,
      nodes: [update],
      attributes: [
        { attr: UID },
        { attr: accountConfig.edges.activeSlug, model: accountSlugConfig.model, children: accountSlugChildren },
      ],
    },
  ];
};

// Nebula only ever holds the tenant we are sitting on, so this goes around it: the tenant header
// addresses the account we just created while we are still on the old one.
const nameAccount = async (account, infos) => {
  const response = await dogma.bulkMutate(
    namePayloads(account, infos),
    { header: { 'x-tenant-id': String(account.uid) } },
  );
  if (response?.status !== 200) {
    throw new Error(`error naming account ${account.uid}: ${response?.status}`);
  }

  const slug = response.data.updateAccount?.[0]?.activeSlug?.slug;
  if (!slug) {
    throw new Error(`account ${account.uid} was named without a slug`);
  }
  return slug;
};

const createAccount = async (infos) => {
  visible.value = false;
  showOverlaySpinner(t('moduleSelectionInfos.creatingYourAccount'));
  try {
    const account = provisionedAccount ?? await provision();
    if (account === null) {
      visible.value = true;
      return false;
    }
    provisionedAccount = account;

    const tenantSlug = await nameAccount(account, infos);
    provisionedAccount = null;

    // Entering last keeps every recoverable failure on this account, where the dialog can come
    // back with what the user typed. It is a plain switch by now: the account is named, so it
    // lands in the app rather than in onboarding.
    return await switchAccount({ uid: account.uid }, { name: routeName.goalsExplorer, params: { tenantSlug } });
  } catch (error) {
    logCatch(() => snackbar.error())(error);
    visible.value = true;
    return false;
  } finally {
    hideOverlaySpinner();
  }
};

const onImageCropped = (blob) => {
  uploadedImage = uploadFile(blob).then((data) => {
    imageUrl.value = data.getURL;
    return data;
  }).catch(logCatch(() => {
    snackbar.error();
    return null;
  }));
};

const onSubmit = (infos) => {
  companyName.value = infos.companyName;
  companySize.value = infos.companySize;
  Promise.resolve(uploadedImage)
    .then((companyImage) => createAccount({ ...infos, companyImage }))
    .then((created) => {
      if (created) {
        reset();
      }
    });
};

const open = () => { visible.value = true; };

EventBus.$on('show-add-account-dialog', open);
onBeforeUnmount(() => EventBus.$off('show-add-account-dialog', open));
</script>

<style scoped lang="scss" type="text/scss">
  .add-account-dialog {
    padding-bottom: 2.4rem;

    ._header {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 2.4rem;

      ._title {
        font-size: $font-size-6;
        font-weight: $font-weight-medium;
      }

      ._close {
        position: absolute;
        right: 0;
      }
    }
  }
</style>
